import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/PermissionGuard";

import { MembersTabs } from "../components/MembersTabs";
import { MembersTable } from "../components/MembersTable";
import { InviteMemberDialog } from "../components/InviteMemberDialog";
import { EditMemberDialog } from "../components/EditMemberDialog";
import { SearchBar } from "../components/SearchBar";
import { useMembers } from "../../application/hooks/useMember";
import { GroupsTable } from "../components/GroupsTable";
import { CreateGroupDialog } from "../components/CreateGroupDialog";
import { RemoveMemberDialog } from "../components/RemoveMemberDialog";
import { ExtraPermissionsDialog } from "../components/ExtraPermissionsDialog";
import { DeleteGroupDialog } from "../components/DeleteGroupDialog";
import { EditGroupDialog } from "../components/EditGroupDialog";
import { ViewGroupMembersDialog } from "../components/ViewGroupMembersDialog";
import {
  useActivateMemberMutation,
  useGetGroupsQuery,
  useDeactivateMemberMutation,
} from "../../infrastructure/api/membersGroupsApi";
import type { Member } from "../../domain/entities/Member";
import type { Group } from "../../domain/entities/Member";
import { PageShell } from "@/components/layout/PageShell";
import { textStyles } from "@/lib/textStyles";
import { useGetMeQuery } from "@/features/profile/infrastructure/api/ProfileApi";

const MembersGroupsPage = () => {
  const { t } = useTranslation("membersGroups");
  const { can } = usePermissions();
  const [tab, setTab] = useState("members");
  const [open, setOpen] = useState(false);
  const [openCreateGroup, setOpenCreateGroup] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");

  // Member dialog state
  const [editMember, setEditMember] = useState<Member | null>(null);
  const [removeMember, setRemoveMember] = useState<Member | null>(null);
  const [extraPermMember, setExtraPermMember] = useState<Member | null>(null);

  // Group dialog state
  const [editGroup, setEditGroup] = useState<Group | null>(null);
  const [deleteGroup, setDeleteGroup] = useState<Group | null>(null);
  const [viewMembersGroup, setViewMembersGroup] = useState<Group | null>(null);

  const { members, isLoading: membersLoading, setSearch } = useMembers();
  const [deactivateMember] = useDeactivateMemberMutation();
  const [activateMember] = useActivateMemberMutation();
  const { data: profile } = useGetMeQuery();

  const { data: groups = [], isLoading: groupsLoading } = useGetGroupsQuery(
    groupSearch ? { query: groupSearch } : undefined,
  );

  const handleMarkInactive = async (member: Member) => {
    try {
      await deactivateMember(member.id).unwrap();
      toast.success(t("toasts.markedInactive", { name: member.name }));
    } catch {
      toast.error(t("toasts.markedInactiveError"));
    }
  };

  const handleMarkActive = async (member: Member) => {
    try {
      await activateMember(member.id).unwrap();
      toast.success(t("toasts.markedActive", { name: member.name }));
    } catch {
      toast.error(t("toasts.markedActiveError"));
    }
  };

  return (
    <PageShell className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="shrink-0 space-y-4">
        <h1 className="text-gray-800" style={textStyles.heading2}>
          {t("pageTitle")}
        </h1>
        <MembersTabs value={tab} onChange={setTab} />
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          {tab === "members" && <SearchBar onChange={setSearch} />}
          {tab === "groups" && <SearchBar onChange={setGroupSearch} />}

          {tab === "members" && (
            <PermissionGuard allowed={can("add_member")}>
              <Button
                onClick={() => setOpen(true)}
                className="h-10 w-full bg-primary text-white hover:bg-primary cursor-pointer sm:w-auto"
              >
                <Plus className="w-4 h-4" />
                {t("invite")}
              </Button>
            </PermissionGuard>
          )}

          {tab === "groups" && (
            <PermissionGuard allowed={can("group_create")}>
              <Button
                onClick={() => setOpenCreateGroup(true)}
                className="h-10 w-full bg-primary text-white hover:bg-primary cursor-pointer sm:w-auto"
              >
                <Plus className="w-4 h-4" />
                {t("createGroup")}
              </Button>
            </PermissionGuard>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {tab === "members" && (
          <div className="min-h-0 flex-1 overflow-y-auto">
          <MembersTable
            members={members}
            isLoading={membersLoading}
            onView={() => {}}
            onEdit={(member) => setEditMember(member)}
            onRemove={(member) => setRemoveMember(member)}
            onMarkInactive={handleMarkInactive}
            onMarkActive={handleMarkActive}
            onExtraPermissions={(member) => setExtraPermMember(member)}
            canView={can("read_member")}
            canEdit={can("update_member")}
            canMarkInactive={can("update_member")}
            canMarkActive={can("update_member")}
            canRemove={can("delete_member")}
            currentUserId={profile?.id}
          />
          </div>
        )}

        {tab === "groups" && (
          <div className="min-h-0 flex-1 overflow-y-auto">
          <GroupsTable
            groups={groups}
            isLoading={groupsLoading}
            onEdit={(group) => setEditGroup(group)}
            onDelete={(group) => setDeleteGroup(group)}
            onViewMembers={(group) => setViewMembersGroup(group)}
            canEdit={can("group_update")}
            canDelete={can("group_delete")}
          />
          </div>
        )}
      </div>

      {/* Member dialogs */}
      <InviteMemberDialog open={open} onOpenChange={setOpen} />
      <RemoveMemberDialog
        open={!!removeMember}
        onOpenChange={(val) => { if (!val) setRemoveMember(null); }}
        member={removeMember}
      />
      <EditMemberDialog
        open={!!editMember}
        onOpenChange={(val) => { if (!val) setEditMember(null); }}
        member={editMember}
      />
      <ExtraPermissionsDialog
        open={!!extraPermMember}
        onOpenChange={(val) => { if (!val) setExtraPermMember(null); }}
        member={extraPermMember}
      />

      {/* Group dialogs */}
      <EditGroupDialog
        open={!!editGroup}
        onOpenChange={(val) => { if (!val) setEditGroup(null); }}
        group={editGroup}
      />
      <DeleteGroupDialog
        open={!!deleteGroup}
        onOpenChange={(val) => { if (!val) setDeleteGroup(null); }}
        group={deleteGroup}
      />
      <ViewGroupMembersDialog
        open={!!viewMembersGroup}
        onOpenChange={(val) => { if (!val) setViewMembersGroup(null); }}
        group={viewMembersGroup}
        canViewMember={can("read_member")}
      />

      <CreateGroupDialog
        open={openCreateGroup}
        onOpenChange={setOpenCreateGroup}
      />
    </PageShell>
  );
};

export default MembersGroupsPage;
